import {
  useRef,
  useEffect,
  useLayoutEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import rough from "roughjs/bin/rough";

import { THEME, arrayToMap, toBrandedType } from "@excalidraw/common";
import {
  doBoundsIntersect,
  getAnimationOverride,
  getElementBounds,
  getElementsOverlappingFrame,
  getFrameLikeElements,
} from "@excalidraw/element";
import { useExcalidrawAPI } from "@excalidraw/excalidraw";
import { useUIAppState } from "@excalidraw/excalidraw/context/ui-appState";
import { renderStaticScene } from "@excalidraw/excalidraw/renderer/staticScene";

import type {
  ExcalidrawElement,
  NonDeletedSceneElementsMap,
} from "@excalidraw/element/types";
import type { RenderableElementsMap } from "@excalidraw/excalidraw/scene/types";
import type { AppState } from "@excalidraw/excalidraw/types";

import { getElementAnimations } from "../animation/types";

import { useActiveClip } from "./ActiveClipContext";
import {
  FLIPBOOK_TICK_EVENT,
  ensureImageCache,
  sharedImageCache,
} from "./FlipbookBar";

import "./AnimationPreviewWindow.scss";

import type { AnimationRuntime } from "../animation/runtime";

interface AnimationPreviewWindowProps {
  runtime: AnimationRuntime | null;
  isPlaying: boolean;
}

// max canvas box; actual canvas adopts the active screen's aspect ratio
const MAX_WIDTH = 320;
const MAX_HEIGHT = 180;
const PADDING = 16;
// window chrome (header + screen flipper) on top of the canvas box
const CHROME_HEIGHT = 76;
const MARGIN = 16;

/** Where the user last dropped it. The window unmounts when the preview is
 * hidden, so component state alone would spring it back to the default spot
 * on every toggle. */
let lastPosition: { x: number; y: number } | null = null;

/** Opening spot only: bottom-center, clear of whichever bottom docks
 * (animation bar, flipbook filmstrip) happen to be mounted. Measured once, at
 * open — after that nothing but the user moves the window, so a dock opening
 * later never shoves it. */
const defaultPosition = () => {
  let reserve = 0;
  for (const selector of [".flipbook-dock", ".canvas-animation-dock"]) {
    const dock = document.querySelector(selector);
    if (dock) {
      reserve = Math.max(
        reserve,
        window.innerHeight - dock.getBoundingClientRect().top,
      );
    }
  }
  return {
    x: (window.innerWidth - MAX_WIDTH) / 2,
    y: window.innerHeight - MAX_HEIGHT - CHROME_HEIGHT - MARGIN - reserve,
  };
};

/** The only constraint on a free-floating window: it can sit anywhere over the
 * canvas — docks included — but never fully off-screen where it can't be
 * grabbed back. Measures the real element so a short window (no screen pager)
 * can still reach the bottom edge. */
export const clampToViewport = (
  x: number,
  y: number,
  el: HTMLElement | null,
) => {
  const width = el?.offsetWidth || MAX_WIDTH;
  const height = el?.offsetHeight || MAX_HEIGHT + CHROME_HEIGHT;
  return {
    x: Math.max(MARGIN, Math.min(x, window.innerWidth - width - MARGIN)),
    y: Math.max(MARGIN, Math.min(y, window.innerHeight - height - MARGIN)),
  };
};

export const AnimationPreviewWindow = ({
  runtime: _runtime,
  isPlaying,
}: AnimationPreviewWindowProps) => {
  const excalidrawAPI = useExcalidrawAPI();
  const appState = useUIAppState();
  const { previewVisible, setPreviewVisible } = useActiveClip();
  // exactly where the user put it — free-floating, nothing but a drag or a
  // viewport shrink ever moves it
  const [position, setPosition] = useState(
    () => lastPosition ?? defaultPosition(),
  );
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  // once the user drags, their spot is sacred; until then the default
  // position re-measures on every show so it clears whichever dock is up
  const userMovedRef = useRef(lastPosition !== null);
  const [screenIndex, setScreenIndex] = useState(0);
  // driven by FLIPBOOK_TICK_EVENT — the flipbook is a second player, and the
  // header's status is the only thing telling the user which one is running
  const [flipbookPlaying, setFlipbookPlaying] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const windowRef = useRef<HTMLDivElement>(null);

  // getSceneElements returns a fresh array reference per scene version, so
  // these memos re-run exactly when the scene actually changes
  const sceneElements = excalidrawAPI?.getSceneElements();
  const elements = useMemo(() => sceneElements ?? [], [sceneElements]);
  const animatedElements = useMemo(
    () => elements.filter((el) => getElementAnimations(el).length > 0),
    [elements],
  );
  const animatedCount = animatedElements.length;

  // "screens" are frame elements (see ScreenMenu) — the preview flips
  // through them like a flipbook
  const frames = useMemo(() => getFrameLikeElements(elements), [elements]);
  // renderPreview runs per playback frame — building these maps there means
  // two full-scene allocations every tick
  const elementsMap = useMemo(() => arrayToMap(elements), [elements]);
  const activeIndex = frames.length
    ? Math.min(screenIndex, frames.length - 1)
    : 0;
  const activeScreen = frames[activeIndex] ?? null;

  // canvas adopts the active screen's aspect ratio within the max box
  let canvasWidth = MAX_WIDTH;
  let canvasHeight = MAX_HEIGHT;
  if (activeScreen && activeScreen.width > 0 && activeScreen.height > 0) {
    const scale = Math.min(
      MAX_WIDTH / activeScreen.width,
      MAX_HEIGHT / activeScreen.height,
    );
    canvasWidth = Math.max(1, Math.round(activeScreen.width * scale));
    canvasHeight = Math.max(1, Math.round(activeScreen.height * scale));
  }

  /** Draws the preview canvas. With a screen (frame) active, renders the
   * screen's viewport 1:1 (contents clipped to the frame, like the real
   * screen); otherwise falls back to fitting all animated elements. Uses the
   * real static-scene renderer — it takes ONE config object; a positional
   * call routes garbage into the shared throttled render slot (App.tsx sets
   * EXCALIDRAW_THROTTLE_RENDER) and starves the MAIN canvas of its render
   * args, freezing it. Global animation overrides apply here exactly as on
   * the main canvas. */
  const renderPreview = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !excalidrawAPI) {
      return;
    }
    if (!activeScreen && animatedElements.length === 0) {
      return;
    }
    let minX: number;
    let minY: number;
    let contentWidth: number;
    let contentHeight: number;
    let padding: number;
    if (activeScreen) {
      minX = activeScreen.x;
      minY = activeScreen.y;
      contentWidth = activeScreen.width || 100;
      contentHeight = activeScreen.height || 100;
      padding = 0;
    } else {
      // fit the animated elements' rest bounds; playback offsets typically
      // stay in the same neighborhood
      minX = Infinity;
      minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (const el of animatedElements) {
        minX = Math.min(minX, el.x);
        minY = Math.min(minY, el.y);
        maxX = Math.max(maxX, el.x + el.width);
        maxY = Math.max(maxY, el.y + el.height);
      }
      contentWidth = maxX - minX || 100;
      contentHeight = maxY - minY || 100;
      padding = PADDING;
    }
    const zoomValue = Math.min(
      (canvasWidth - padding * 2) / contentWidth,
      (canvasHeight - padding * 2) / contentHeight,
      4,
    );
    // screen = (scene + scroll) * zoom → scroll that centers the content
    const scrollX = -minX + (canvasWidth / zoomValue - contentWidth) / 2;
    const scrollY = -minY + (canvasHeight / zoomValue - contentHeight) / 2;

    // scope to what the screen actually shows. `frameRendering.clip` only
    // clips elements that are ASSIGNED to the frame — anything unassigned
    // (a stray line parked next to the artboard) would otherwise be painted
    // straight over the preview. Same helper the video export uses, so the
    // preview and the exported file agree on the screen's contents.
    let visibleElements = animatedElements as readonly ExcalidrawElement[];
    if (activeScreen) {
      const map = elementsMap;
      const resting = getElementsOverlappingFrame(elements, activeScreen, map);
      // an animation can MOVE an element into this screen (dx/dy overrides) —
      // most visibly a flipbook, whose pages all swap onto one stage frame.
      // Those elements sit far away at rest, so the overlap test above misses
      // them and the preview would go blank on every page but the stage's own.
      // Their frame comes along the same way, carrying its clip region.
      const stageBounds = getElementBounds(activeScreen, map);
      const restingIds = new Set(resting.map((el) => el.id));
      const movedOntoScreen = (el: ExcalidrawElement) => {
        const anim = getAnimationOverride(el.id);
        if (!anim?.dx && !anim?.dy) {
          return false;
        }
        const shifted = {
          ...el,
          x: el.x + (anim.dx ?? 0),
          y: el.y + (anim.dy ?? 0),
        };
        return doBoundsIntersect(getElementBounds(shifted, map), stageBounds);
      };
      visibleElements = elements.filter(
        (el) => restingIds.has(el.id) || movedOntoScreen(el),
      );
    }

    renderStaticScene(
      {
        canvas,
        rc: rough.canvas(canvas),
        elementsMap: toBrandedType<RenderableElementsMap>(
          arrayToMap(visibleElements),
        ),
        allElementsMap: toBrandedType<NonDeletedSceneElementsMap>(elementsMap),
        visibleElements,
        scale: window.devicePixelRatio,
        appState: {
          ...appState,
          // the artwork's own colors, never the UI theme (see staticScene.ts):
          // isExporting here only means "bypass the shape cache"
          theme: THEME.LIGHT,
          scrollX,
          scrollY,
          zoom: { value: zoomValue },
          shouldCacheIgnoreZoom: false,
          selectedElementIds: {},
          frameRendering: {
            // with a screen active, clip its members to the frame like the
            // real screen; never draw frame outlines/names in the preview
            enabled: !!activeScreen,
            outline: false,
            name: false,
            clip: !!activeScreen,
          },
        } as AppState,
        renderConfig: {
          canvasBackgroundColor: appState.viewBackgroundColor,
          imageCache: sharedImageCache,
          renderGrid: false,
          isExporting: true,
          embedsValidationStatus: new Map(),
          elementsPendingErasure: new Set(),
          pendingFlowchartNodes: null,
          theme: THEME.LIGHT,
        },
      },
      // un-throttled: sharing the throttled slot with the main canvas would
      // clobber the main canvas's pending render args
      false,
    );
  }, [
    excalidrawAPI,
    elements,
    elementsMap,
    animatedElements,
    activeScreen,
    canvasWidth,
    canvasHeight,
    appState,
  ]);

  // static refresh whenever the scene/app state changes
  useEffect(() => {
    renderPreview();
    // image/texture files decode async — repaint once they land in the cache
    if (excalidrawAPI) {
      ensureImageCache(elements, excalidrawAPI.getFiles(), renderPreview);
    }
  }, [renderPreview, excalidrawAPI, elements]);

  // playback: re-render so the GSAP overrides show live. Every OTHER frame
  // (a thumbnail at 30fps is imperceptible), and NOT at all once a render
  // proves expensive: the preview bypasses the shape cache (isExporting
  // above), so on big scenes a single render can cost several frames — it
  // would starve the main canvas, which is where the animation actually
  // plays. Frozen preview keeps its last painted frame.
  // ponytail: freeze-over-budget; OffscreenCanvas worker if live preview at
  // scale ever matters
  useEffect(() => {
    if (!isPlaying) {
      return;
    }
    let frameId: number;
    let frameCount = 0;
    let frozen = false;
    const tick = () => {
      if (!frozen && frameCount++ % 2 === 0) {
        const t0 = performance.now();
        renderPreview();
        frozen = performance.now() - t0 > 8;
      }
      frameId = requestAnimationFrame(tick);
    };
    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [isPlaying, renderPreview]);

  // flipbook playback: each page swap fires a tick — follow the stage frame
  // and repaint so the preview flips along with the canvas
  useEffect(() => {
    const onTick = (e: Event) => {
      const stageId = (e as CustomEvent).detail?.stageId;
      // the stop tick carries no stage — the header's ▶ follows either player
      setFlipbookPlaying(Boolean(stageId));
      if (stageId) {
        const index = frames.findIndex((f) => f.id === stageId);
        if (index !== -1 && index !== screenIndex) {
          setScreenIndex(index); // re-render via the static-refresh effect
          return;
        }
      }
      renderPreview();
    };
    window.addEventListener(FLIPBOOK_TICK_EVENT, onTick);
    return () => window.removeEventListener(FLIPBOOK_TICK_EVENT, onTick);
  }, [frames, screenIndex, renderPreview]);

  // remember the spot across close/reopen — but only once the user has
  // actually dragged it. Persisting the computed default here froze a stale
  // measurement taken before the docks mounted, so the window reopened on
  // top of the flipbook/animation bar and swallowed their clicks.
  useEffect(() => {
    if (userMovedRef.current) {
      lastPosition = position;
    }
  }, [position]);

  // handle window dragging
  useEffect(() => {
    if (!isDragging) {
      return;
    }
    const handlePointerMove = (e: PointerEvent) => {
      userMovedRef.current = true;
      setPosition(
        clampToViewport(
          e.clientX - dragOffset.x,
          e.clientY - dragOffset.y,
          windowRef.current,
        ),
      );
    };
    const handlePointerUp = () => {
      setIsDragging(false);
    };
    document.addEventListener("pointermove", handlePointerMove);
    document.addEventListener("pointerup", handlePointerUp);
    document.addEventListener("pointercancel", handlePointerUp);
    return () => {
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", handlePointerUp);
      document.removeEventListener("pointercancel", handlePointerUp);
    };
  }, [isDragging, dragOffset]);

  // the component mounts hidden long before any dock exists, so the useState
  // initializer measures an empty layout — and the docks live in sibling
  // React roots that can commit a beat later than this one. Until the user
  // drags, converge on the default spot whenever visibility or the page
  // count changes (the dock's height tracks the page count); the rAF defers
  // the measurement past every root's commit for that frame.
  const visible = previewVisible && (animatedCount > 0 || frames.length > 0);
  useLayoutEffect(() => {
    if (!visible || userMovedRef.current) {
      return;
    }
    const id = requestAnimationFrame(() => {
      if (!userMovedRef.current) {
        setPosition(defaultPosition());
      }
    });
    return () => cancelAnimationFrame(id);
  }, [visible, frames.length, animatedCount]);

  // a shrinking viewport can strand the window off-screen; nothing else pulls
  // it back, which is the point
  useEffect(() => {
    const onResize = () =>
      setPosition((p) => clampToViewport(p.x, p.y, windowRef.current));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // hooks all ran — safe to bail from rendering now (early-returning before
  // the effects above is a conditional-hooks crash when visibility toggles)
  if (!visible) {
    return null;
  }

  const handlePointerDown = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest("button")) {
      return; // controls still click; everything else is a drag handle
    }
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const flipScreen = (direction: 1 | -1) => {
    if (frames.length < 2) {
      return;
    }
    setScreenIndex((activeIndex + direction + frames.length) % frames.length);
  };

  return (
    <div
      ref={windowRef}
      className="animation-preview-window"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        cursor: isDragging ? "grabbing" : "grab",
      }}
      onPointerDown={handlePointerDown}
    >
      <div className="animation-preview-window__header">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.25rem",
            flex: 1,
          }}
        >
          <span className="animation-preview-window__title">
            {activeScreen
              ? activeScreen.name || `Screen ${activeIndex + 1}`
              : "Preview"}
          </span>
          <span
            className={`animation-preview-window__status${
              isPlaying || flipbookPlaying
                ? " animation-preview-window__status--playing"
                : ""
            }`}
          >
            {isPlaying || flipbookPlaying ? "▶" : "⏸"}
          </span>
        </div>
        <button
          className="animation-preview-window__close"
          onClick={() => setPreviewVisible(false)}
          title="Close"
        >
          ✕
        </button>
      </div>
      <div className="animation-preview-window__content">
        <canvas
          // remount per screen so the flip-in animation plays on switch
          key={activeScreen?.id ?? "animated"}
          ref={canvasRef}
          width={canvasWidth * window.devicePixelRatio}
          height={canvasHeight * window.devicePixelRatio}
          style={{ width: canvasWidth, height: canvasHeight }}
          className="animation-preview-window__canvas"
        />
      </div>
      {frames.length > 1 && (
        <div className="animation-preview-window__screens">
          <button
            className="animation-preview-window__flip"
            title="Previous screen"
            onClick={() => flipScreen(-1)}
          >
            ‹
          </button>
          <span className="animation-preview-window__screen-label">
            {activeIndex + 1} / {frames.length}
          </span>
          <button
            className="animation-preview-window__flip"
            title="Next screen"
            onClick={() => flipScreen(1)}
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
};
